/**
 * Rendering the server's `Email Template` for outgoing RFQ mail.
 *
 * ---- Why we render it rather than compose our own ----
 *
 * The `Rfq` template on the bench is not a greeting, it is policy: it tells the supplier the
 * quotation MUST come back on the provided workbook, that any other format is rejected, and that
 * the file's structure must not be changed. Our own `defaultRfqNote` said none of that. A supplier
 * who returns a PDF because nobody told them not to is a real cost, and the rule belongs to the
 * business, not to this codebase.
 *
 * Rendering it here — rather than letting the server do it — is forced:
 * `send_rfq_email_with_excel` does NOT use the template. Sending with no `message` produced its own
 * hardcoded default instead (measured 2026-08-07). So the template reaches the supplier only if we
 * fill it in and pass it as `message`.
 *
 * ---- Plain text, not HTML ----
 *
 * The stored `response` is Quill HTML. We render to PLAIN TEXT because that is what the purchaser
 * edits in the send dialog, and `textToHtml` (`lib/rfq.ts`) puts the markup back on the way out.
 * Handing someone `<div class="ql-editor">` to edit is how a malformed mail reaches a supplier.
 *
 * ---- Unknown placeholders ----
 *
 * `portal_link` and `update_password_link` are being removed from the template by the business.
 * Until they are, an unresolved placeholder renders as EMPTY rather than as its own literal text:
 * a supplier receiving `{{ portal_link }}` is worse than receiving nothing, and worse than us
 * inventing a URL. `{% if %}` blocks around an unset variable are dropped whole.
 */

/** Values a caller can substitute. Anything absent resolves through the template's own `or`
 *  default, then to empty. */
export interface TemplateVars {
  supplier_name?: string
  supplier_salutation?: string
  message_for_supplier?: string
  user_fullname?: string
  [key: string]: string | undefined
}

/** `{{ name }}` / `{{ name or "fallback" }}` — the two forms the RFQ template uses. */
const PLACEHOLDER = /\{\{\s*([a-zA-Z_][\w.]*)\s*(?:or\s*(?:"([^"]*)"|'([^']*)'))?\s*\}\}/g
/** `{% if x %} … {% endif %}` — kept only when `x` resolves to something non-empty. */
const IF_BLOCK = /\{%\s*if\s+([a-zA-Z_][\w.]*)\s*%\}([\s\S]*?)\{%\s*endif\s*%\}/g

/** Quill HTML → the plain text a purchaser edits. Block tags become newlines, the rest is dropped. */
export function templateHtmlToText(html: string): string {
  const withBreaks = html
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\/\s*(p|div|li|tr|h[1-6])\s*>/gi, '\n')
    .replace(/<\s*li[^>]*>/gi, '• ')
  return decodeEntities(withBreaks.replace(/<[^>]+>/g, ''))
    .split('\n')
    .map((line) => line.replace(/[ \t ]+/g, ' ').trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

function decodeEntities(s: string): string {
  return s
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
}

const resolve = (vars: TemplateVars, name: string): string => (vars[name] ?? '').trim()

/**
 * Fill a template's stored `response` and return the plain text to put in the send dialog.
 *
 * Order matters: `{% if %}` blocks are resolved BEFORE placeholders, so a dropped block's
 * placeholders never render at all.
 */
export function renderEmailTemplate(response: string, vars: TemplateVars): string {
  const withBlocks = response.replace(IF_BLOCK, (_m, name: string, body: string) =>
    resolve(vars, name) === '' ? '' : body,
  )
  const filled = withBlocks.replace(PLACEHOLDER, (_m, name: string, dq?: string, sq?: string) => {
    const value = resolve(vars, name)
    if (value !== '') return value
    return dq ?? sq ?? ''
  })
  return templateHtmlToText(filled)
}
